import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type Channel, type HistoryEntry } from '../lib/api';
import { ChannelTag, Change } from './ChangeBits';

/**
 * Everything anyone has pushed to a channel, newest first, plus a second view
 * of the sheet-driven pulls that stage those changes in the first place.
 *
 * The per-listing history in the expanded row answers "what happened to this
 * listing". The "Channel Changes" view answers the question you actually ask
 * when a price looks wrong: what have we been changing, where did it go, and
 * who did it. So every entry leads with its channel, states the change as
 * old → new rather than making the reader diff two columns, and names the
 * person.
 *
 * Failed and reverted writes are the ones still needing attention, so they are
 * marked and can be isolated with one filter.
 *
 * "External Syncing" is a separate view rather than more filters on the same
 * table: a SheetSyncRun is a pull that stages changes, not a pushed change
 * itself, so it has no channel, field, or old/new value to show in those
 * columns.
 */

export function HistoryTab() {
  const [view, setView] = useState<'changes' | 'sync'>('changes');

  return (
    <div>
      <div className="toolbar">
        <button className={view === 'changes' ? 'active' : ''} onClick={() => setView('changes')}>
          Channel Changes
        </button>
        <button className={view === 'sync' ? 'active' : ''} onClick={() => setView('sync')}>
          External Syncing
        </button>
      </div>
      {view === 'changes' ? <ChannelChanges /> : <ExternalSyncing />}
    </div>
  );
}

function ChannelChanges() {
  const [channelId, setChannelId] = useState('');
  const [field, setField] = useState('');
  const [outcome, setOutcome] = useState<'all' | 'problems'>('all');

  const channelsQuery = useQuery({ queryKey: ['channels'], queryFn: api.channels });
  const { data, isLoading, error } = useQuery({
    queryKey: ['history-all', channelId, field, outcome],
    queryFn: () => api.historyAll({ channelId: channelId || undefined, field: field || undefined, outcome }),
  });

  const channels = useMemo(
    () => (channelsQuery.data ?? []).slice().sort((a, b) => a.sortOrder - b.sortOrder),
    [channelsQuery.data],
  );

  if (isLoading) return <div className="empty-state">Loading history…</div>;
  if (error) return <div className="alert error">{(error as Error).message}</div>;

  const entries = data?.entries ?? [];

  return (
    <div>
      <div className="toolbar">
        <select value={channelId} onChange={(e) => setChannelId(e.target.value)} aria-label="Channel">
          <option value="">All channels</option>
          {channels.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>

        <select value={field} onChange={(e) => setField(e.target.value)} aria-label="Field">
          {/* The unfiltered view already includes every field, sku included —
              this option was misnamed rather than actually restrictive. */}
          <option value="">All fields</option>
          <option value="price">Price only</option>
          <option value="title">Title only</option>
          <option value="sku">SKU only</option>
        </select>

        <select
          value={outcome}
          onChange={(e) => setOutcome(e.target.value as 'all' | 'problems')}
          aria-label="Outcome"
        >
          <option value="all">All outcomes</option>
          <option value="problems">Failed or reverted</option>
        </select>

        <span className="muted small">
          {entries.length} of {data?.total ?? 0} changes
        </span>
      </div>

      {entries.length === 0 ? (
        <div className="empty-state">
          <p>No changes recorded yet.</p>
          <p className="small">
            Every change pushed to a channel is logged here, with who pushed it.
          </p>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="grid history">
            <thead>
              <tr>
                <th>When</th>
                <th>Channel</th>
                <th>Product</th>
                <th>Change</th>
                <th>By</th>
                <th>Outcome</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id}>
                  <td className="small muted nowrap">{new Date(entry.createdAt).toLocaleString()}</td>
                  <td className="nowrap">
                    <ChannelTag name={entry.channelName} type={entry.channelType} />
                  </td>
                  <td className="title-cell">
                    <div>{entry.productTitle ?? entry.listingTitle}</div>
                    {entry.sku && <div className="sku">{entry.sku}</div>}
                  </td>
                  <td>
                    <Change
                      field={entry.field}
                      oldValue={entry.oldValue}
                      newValue={entry.newValue}
                      currency={entry.currency}
                      businessNewValue={entry.expectedBusinessPriceMinor}
                    />
                  </td>
                  <td className="small nowrap">
                    {entry.user ? (
                      entry.user.name || entry.user.email
                    ) : (
                      <span className="muted">deleted user</span>
                    )}
                  </td>
                  <td className="nowrap">
                    <Outcome entry={entry} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * Past pulls of the Google Sheet — what it read and staged, not what reached
 * a channel. A row here becomes visible changes in "Channel Changes" only
 * once someone runs the queue.
 */
function ExternalSyncing() {
  const { data: runs, isLoading, error } = useQuery({
    queryKey: ['sheet-history'],
    // Wrapped rather than passed directly: react-query hands the query context
    // to queryFn, which would arrive as sheetHistory's includePreviews flag.
    queryFn: () => api.sheetHistory(),
  });

  if (isLoading) return <div className="empty-state">Loading history…</div>;
  if (error) return <div className="alert error">{(error as Error).message}</div>;

  if (!runs || runs.length === 0) {
    return (
      <div className="empty-state">
        <p>No sheet syncs recorded yet.</p>
        <p className="small">Every pull of the Google Sheet, scheduled or manual, is logged here.</p>
      </div>
    );
  }

  return (
    <div className="table-wrap">
      <table className="grid history">
        <thead>
          <tr>
            <th>Started</th>
            <th>Rows read</th>
            <th>Staged</th>
            <th>Unchanged</th>
            <th>Unmatched</th>
            <th>Collisions</th>
            <th>Triggered by</th>
            <th>Outcome</th>
          </tr>
        </thead>
        <tbody>
          {runs.map((run) => (
            <tr key={run.id}>
              <td className="small muted nowrap">{new Date(run.startedAt).toLocaleString()}</td>
              <td>{run.rowsRead}</td>
              <td>{run.staged}</td>
              <td>{run.unchanged}</td>
              <td>{run.unmatched}</td>
              <td>{run.collisions}</td>
              <td className="small nowrap">
                {run.triggeredBy ?? <span className="muted">scheduled</span>}
              </td>
              <td className="nowrap">
                {!run.finishedAt ? (
                  <span className="badge" title="Still running">
                    running
                  </span>
                ) : run.ok ? (
                  <span className="badge ok">ok</span>
                ) : (
                  <span className="badge error">failed</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * What became of the write.
 *
 * `verified` means a re-read confirmed the value is actually live, which is a
 * stronger claim than "we sent it" and worth distinguishing.
 */
function Outcome({ entry }: { entry: HistoryEntry }) {
  if (entry.revertedAt) {
    return (
      <span className="badge warn" title={`Reverted ${new Date(entry.revertedAt).toLocaleString()}`}>
        reverted
      </span>
    );
  }
  if (entry.status === 'failed') return <span className="badge error">failed</span>;
  if (entry.status === 'verified') {
    return (
      <span
        className="badge ok"
        title={
          entry.verifiedAt
            ? `Confirmed live ${new Date(entry.verifiedAt).toLocaleString()}`
            : 'Confirmed live on the channel'
        }
      >
        verified
      </span>
    );
  }
  return (
    <span className="badge" title="Sent, awaiting confirmation from the channel">
      {entry.status}
    </span>
  );
}
